import React, { useState, useEffect } from 'react';
import { X, Users, Copy, RefreshCw, Trash2, Settings } from 'lucide-react';
import { useTeams } from '../hooks/useTeams';
import { useAuth } from '../contexts/AuthContext';
import type { TeamCreate, TeamUpdate, TeamWithMembers, TeamMemberUpdate } from '../types';

interface TeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  team?: TeamWithMembers | null; // Updated to use TeamWithMembers
  onSuccess?: (actionType?: 'create' | 'update' | 'delete') => void;
}

export const TeamModal: React.FC<TeamModalProps> = ({
  isOpen,
  onClose,
  team,
  onSuccess
}) => {
  const { user } = useAuth();
  const { createTeam, updateTeam, deleteTeam, regenerateJoinCode, updateMember, isCreating, isUpdating, isDeleting, isRegeneratingCode, isUpdatingMember } = useTeams();
  const [formData, setFormData] = useState<TeamCreate>({
    name: '',
    display_name: '',
    description: '',
    allow_member_invites: false,
    require_approval: true,
    allow_personal_task_visibility: false,
  });

  // Personal settings state
  const [personalSettings, setPersonalSettings] = useState({
    show_personal_tasks: false,
    personal_tasks_visible_to_team: false,
  });

  const [showJoinCode, setShowJoinCode] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isEditing = !!team;
  const title = isEditing ? 'Team Settings' : 'Create Team';

  // Find current user's membership in the team
  const currentUserMember = team && user ? team.members.find(member => member.user_id === user.id) : null;
  const isOwner = team && user ? team.owner_id === user.id : false;
  const isAdmin = currentUserMember?.role === 'admin' || isOwner;

  // Reset form when modal opens/closes or team changes
  useEffect(() => {
    if (isOpen) {
      if (team) {
        setFormData({
          name: team.name,
          display_name: team.display_name || '',
          description: team.description || '',
          allow_member_invites: team.allow_member_invites,
          require_approval: team.require_approval,
          allow_personal_task_visibility: team.allow_personal_task_visibility,
        });
        setShowJoinCode(!!team.join_code);

        // Set personal settings if user is a member
        if (currentUserMember) {
          setPersonalSettings({
            show_personal_tasks: currentUserMember.show_personal_tasks,
            personal_tasks_visible_to_team: currentUserMember.personal_tasks_visible_to_team,
          });
        }
      } else {
        setFormData({
          name: '',
          display_name: '',
          description: '',
          allow_member_invites: false,
          require_approval: true,
          allow_personal_task_visibility: false,
        });
        setShowJoinCode(false);
        setPersonalSettings({
          show_personal_tasks: false,
          personal_tasks_visible_to_team: false,
        });
      }
    }
    setCopiedCode(false);
    setShowDeleteConfirm(false);
  }, [isOpen, team, currentUserMember]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isEditing && team) {
        await updateTeam({ id: team.id, data: formData as TeamUpdate });
        onSuccess?.('update');
      } else {
        await createTeam(formData);
        onSuccess?.('create');
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to save team:', error);
      // Don't close modal on error so user can retry
    }
  };

  const handlePersonalSettingsUpdate = async (newSettings: Partial<typeof personalSettings>) => {
    if (!team || !currentUserMember) return;

    try {
      const updatedSettings = { ...personalSettings, ...newSettings };
      setPersonalSettings(updatedSettings);

      await updateMember({
        teamId: team.id,
        memberId: currentUserMember.id,
        data: updatedSettings as TeamMemberUpdate
      });
    } catch (error) {
      console.error('Failed to update personal settings:', error);
      // Revert the state on error
      setPersonalSettings(personalSettings);
    }
  };

  const handleDelete = async () => {
    if (!team) return;
    
    try {
      await deleteTeam(team.id);
      onSuccess?.('delete');
      onClose();
    } catch (error) {
      console.error('Failed to delete team:', error);
      // Don't close modal on error so user can retry
    }
  };

  const handleRegenerateCode = async () => {
    if (!team) return;
    
    try {
      await regenerateJoinCode(team.id);
      // The team data will be updated through the query invalidation
    } catch (error) {
      console.error('Failed to regenerate join code:', error);
    }
  };

  const copyJoinCode = async () => {
    if (team?.join_code) {
      await navigator.clipboard.writeText(team.join_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const isLoading = isCreating || isUpdating || isDeleting || isUpdatingMember;

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        // Close modal when clicking the backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Team Settings Section - Only show for admins/owners when editing, or when creating */}
          {(!isEditing || isAdmin) && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Team Settings
              </h3>

              {/* Team Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Team Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter team name"
                  required
                />
              </div>

              {/* Display Name/Emoji */}
              <div>
                <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Display Emoji/Symbol
                </label>
                <input
                  type="text"
                  id="display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="🚀 (optional emoji or symbol)"
                  maxLength={10}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Choose an emoji or short symbol to represent your team
                </p>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Team description (optional)"
                  rows={3}
                />
              </div>

              {/* Team Settings */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Privacy & Permissions</h4>
                
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={formData.require_approval}
                    onChange={(e) => setFormData({ ...formData, require_approval: e.target.checked })}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Require approval for new members</div>
                    <div className="text-xs text-gray-500">
                      New members must be approved before joining
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={formData.allow_member_invites}
                    onChange={(e) => setFormData({ ...formData, allow_member_invites: e.target.checked })}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Allow members to invite others</div>
                    <div className="text-xs text-gray-500">
                      Team members can generate invite codes
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={formData.allow_personal_task_visibility}
                    onChange={(e) => setFormData({ ...formData, allow_personal_task_visibility: e.target.checked })}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Allow personal task sharing</div>
                    <div className="text-xs text-gray-500">
                      Members can choose to share their personal tasks with the team
                    </div>
                  </div>
                </label>
              </div>

              {/* Join Code Section (for editing existing teams) */}
              {isEditing && team && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Team Invite</h4>
                  
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowJoinCode(!showJoinCode)}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      {showJoinCode ? 'Hide' : 'Show'} Join Code
                    </button>
                    
                    {team.join_code && (
                      <button
                        type="button"
                        onClick={handleRegenerateCode}
                        disabled={isRegeneratingCode}
                        className="text-sm text-gray-600 hover:text-gray-700 flex items-center gap-1"
                      >
                        <RefreshCw className={`w-3 h-3 ${isRegeneratingCode ? 'animate-spin' : ''}`} />
                        Regenerate
                      </button>
                    )}
                  </div>

                  {showJoinCode && team.join_code && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <code className="text-sm font-mono bg-white px-2 py-1 rounded border">
                          {team.join_code}
                        </code>
                        <button
                          type="button"
                          onClick={copyJoinCode}
                          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Copy className="w-3 h-3" />
                          {copiedCode ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Share this code with others to invite them to the team
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                {/* Delete Button (only for owners) */}
                {isEditing && isOwner && (
                  <div className="flex-1">
                    {showDeleteConfirm ? (
                      <div className="space-y-2">
                        <p className="text-sm text-red-600">Are you sure? This cannot be undone.</p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleDelete}
                            disabled={isLoading}
                            className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                          >
                            {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowDeleteConfirm(false)}
                            className="flex-1 px-3 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="w-full px-3 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors text-sm flex items-center justify-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Team
                      </button>
                    )}
                  </div>
                )}

                <div className="flex gap-2 flex-1">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  
                  <button
                    type="submit"
                    disabled={isLoading || !formData.name.trim()}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? 'Saving...' : (isEditing ? 'Update Team' : 'Create Team')}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Personal Settings Section - Only show for team members when editing */}
          {isEditing && currentUserMember && (
            <div className="space-y-4 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Your Personal Settings
              </h3>

              <div className="space-y-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={personalSettings.show_personal_tasks}
                    onChange={(e) => handlePersonalSettingsUpdate({ show_personal_tasks: e.target.checked })}
                    disabled={isUpdatingMember}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Show my personal tasks in team view</div>
                    <div className="text-xs text-gray-500">
                      Include your personal tasks when viewing team tasks
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={personalSettings.personal_tasks_visible_to_team}
                    onChange={(e) => handlePersonalSettingsUpdate({ personal_tasks_visible_to_team: e.target.checked })}
                    disabled={isUpdatingMember || !team?.allow_personal_task_visibility}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                  />
                  <div>
                    <div className={`text-sm font-medium ${team?.allow_personal_task_visibility ? 'text-gray-700' : 'text-gray-400'}`}>
                      Share my personal tasks with team members
                    </div>
                    <div className="text-xs text-gray-500">
                      {team?.allow_personal_task_visibility 
                        ? 'Other team members can see your personal tasks'
                        : 'This feature is disabled by team settings'
                      }
                    </div>
                  </div>
                </label>
              </div>

              {isUpdatingMember && (
                <div className="text-sm text-blue-600">
                  Updating settings...
                </div>
              )}

              {/* Save/Close buttons for non-admin users */}
              {!isAdmin && (
                <div className="flex gap-2 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 